import { useState } from 'react';
import parse from 'html-react-parser';
import {
  StayBookFlowFormItemPropertiesModel,
  StayBookFlowWherePropertiesModel,
  StayBookFlowWhenPropertiesModel,
  StayBookFlowHowManyPropertiesModel,
  StayBookFlowWhereElementModelContentType,
  StayBookFlowWhenElementModelContentType,
  StayBookFlowHowManyElementModelContentType,
} from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { ProgressDots } from '@/components/shared/progressDots';
import { FormRow } from '@/components/shared/formRow';
import { useClientTranslation } from '@/lib/core/i18n';
import { formatDate, toUtcMidnight } from '@/lib/core/utils';
import { StayWhereSheet } from './sheets/where';
import { StayWhenSheet } from './sheets/when';
import { MewsHowManySheet } from '../shared/mews/sheets/howMany';
import { STAY_LABELS } from '../shared/labels';
import type { OpenSheet } from '@/services/platform/data/booking/mews/types';
import type { StayBookProps } from './types';

export function StayBook({
  cmsProps,
  booking,
  venueName,
  venues,
  isMember,
  guestsMax,
  ageConfig,
  onBookingChange,
  onBack,
  onContinue,
  step,
}: StayBookProps) {
  const t = useClientTranslation();
  const labels = STAY_LABELS;
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);

  const formItems = (cmsProps?.bookForm?.items ?? []).map(
    item => item.content?.properties as StayBookFlowFormItemPropertiesModel | undefined
  );

  // Stay requires check-out to differ from check-in — always display as a range.
  const whenValue =
    booking.dateFrom && booking.dateTo
      ? `${formatDate(booking.dateFrom.toISOString())} - ${formatDate(booking.dateTo.toISOString())}`
      : '';

  const howManyValue =
    booking.adults > 0 || booking.children > 0
      ? [
          booking.adults > 0 ? `${booking.adults} ${t(labels.adultsCount, { count: booking.adults })}` : '',
          booking.children > 0
            ? `${booking.children} ${t(labels.childrenCount, { count: booking.children })}`
            : '',
        ]
          .filter(Boolean)
          .join(', ')
      : '';

  const rowIsSet = formItems.map(formItem => {
    const stepType = formItem?.stepPicker?.items?.[0]?.content?.contentType;
    switch (stepType) {
      case StayBookFlowWhereElementModelContentType.stayBookFlowWhere:
        return !!venueName;
      case StayBookFlowWhenElementModelContentType.stayBookFlowWhen:
        return !!booking.dateFrom && !!booking.dateTo;
      case StayBookFlowHowManyElementModelContentType.stayBookFlowHowMany:
        return booking.adults > 0 || booking.children > 0;
      default:
        return false;
    }
  });

  const isRowDisabled = (index: number) => index > 0 && !rowIsSet.slice(0, index).every(Boolean);

  // "Where" only opens a picker when there's an actual choice to make — a single (or zero)
  // selectable venue is already auto-filled by the parent, same as event/book.tsx's `hasWhereChoice`.
  const hasVenueChoice =
    venues.filter(v => !((v.membersOnly ?? false) && !isMember) && !(v.isVenueClosed ?? false)).length > 1;

  // Stay requires at least 1 night — check-out must differ from check-in.
  const canContinue =
    !!booking.dateFrom &&
    !!booking.dateTo &&
    booking.dateFrom.toDateString() !== booking.dateTo.toDateString() &&
    booking.adults > 0;
  const titleHtml = (cmsProps?.title as { markup?: string } | undefined)?.markup;

  return (
    <>
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between px-4 py-2">
        <button
          type="button"
          aria-label={t('App.Labels.Back')}
          className="flex size-11 cursor-pointer items-center justify-center"
          onClick={onBack}
        >
          <Icon
            name="long-arrow-up-left"
            width={24}
            height={24}
            strokeWidth={2}
            className="text-jncquoi-neutral-1000"
          />
        </button>
        <ProgressDots step={step} />
      </header>

      {/* Title */}
      {titleHtml && (
        <div className="shrink-0 px-4 pt-6">
          <div className="book-intro-title">{parse(titleHtml)}</div>
        </div>
      )}

      {/* Scrollable content */}
      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-24">
        <div className="flex flex-col gap-6 pt-8">
          {formItems.map((formItem, index) => {
            const step = formItem?.stepPicker?.items?.[0];
            const stepTitle = formItem?.title;
            const stepType = step?.content?.contentType;
            switch (stepType) {
              case StayBookFlowWhereElementModelContentType.stayBookFlowWhere:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.Where')}
                    value={venueName ?? ''}
                    isEmpty={!venueName}
                    disabled={!hasVenueChoice}
                    onClick={() => setOpenSheet('where')}
                    valueClassName="text-jncquoi-neutral-700"
                  />
                );
              case StayBookFlowWhenElementModelContentType.stayBookFlowWhen:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.When')}
                    value={whenValue}
                    isEmpty={!booking.dateFrom || !booking.dateTo}
                    disabled={isRowDisabled(index)}
                    onClick={() => setOpenSheet('when')}
                  />
                );
              case StayBookFlowHowManyElementModelContentType.stayBookFlowHowMany:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.HowMany')}
                    value={howManyValue}
                    isEmpty={booking.adults === 0 && booking.children === 0}
                    disabled={isRowDisabled(index)}
                    onClick={() => setOpenSheet('howmany')}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      </div>

      {/* Sticky bottom button */}
      <div className="fixed right-0 bottom-0 left-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
        <button
          type="button"
          disabled={!canContinue}
          onClick={onContinue}
          className="font-pp-neue-montreal enabled:bg-jncquoi-neutral-1000 disabled:bg-jncquoi-neutral-600 flex h-13 w-full cursor-pointer items-center justify-center rounded-[2px] text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
        >
          {t('App.Labels.Next')}
        </button>
      </div>

      {/* Sheets */}
      {formItems.map((formItem, index) => {
        const step = formItem?.stepPicker?.items?.[0];
        const stepProps = step?.content?.properties;
        const stepType = step?.content?.contentType;
        switch (stepType) {
          case StayBookFlowWhereElementModelContentType.stayBookFlowWhere:
            return (
              <StayWhereSheet
                key={index}
                isOpen={openSheet === 'where'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as StayBookFlowWherePropertiesModel}
                venues={venues}
                selectedVenueKey={booking.venue?.venueNodeKey}
                isMember={isMember}
                onSelect={venue => onBookingChange({ venue, dateFrom: null, dateTo: null, category: null })}
              />
            );
          case StayBookFlowWhenElementModelContentType.stayBookFlowWhen:
            return (
              <StayWhenSheet
                key={index}
                isOpen={openSheet === 'when'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as StayBookFlowWhenPropertiesModel}
                selectedFrom={booking.dateFrom}
                selectedTo={booking.dateTo}
                onSelect={({ from, to }) => {
                  onBookingChange({
                    dateFrom: toUtcMidnight(from),
                    dateTo: toUtcMidnight(to),
                    category: null,
                  });
                  setOpenSheet(null);
                }}
              />
            );
          case StayBookFlowHowManyElementModelContentType.stayBookFlowHowMany:
            return (
              <MewsHowManySheet
                labels={STAY_LABELS}
                borderedValue
                key={index}
                isOpen={openSheet === 'howmany'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as StayBookFlowHowManyPropertiesModel as never}
                ageConfig={ageConfig as never}
                guestsMax={guestsMax}
                numAdults={booking.adults}
                numChildren={booking.children}
                onSelect={(adults, children) => {
                  onBookingChange({ adults, children, category: null });
                  setOpenSheet(null);
                }}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}
